import React, {useEffect, useState} from 'react';
import {Button, message, Table, Popconfirm} from 'antd';
import styled from 'styled-components';
import {
    addDoc,
    collection,
    doc,
    getCountFromServer,
    getDocs,
    limit,
    orderBy,
    query,
    startAfter,
    updateDoc,
    deleteDoc
} from 'firebase/firestore';
import {db} from '../../config/firebase';
import CaseForm from './CaseForm';
import {CaseItem} from '../../types/CardItems';

// 添加样式组件
const ResponsiveContainer = styled.div`
  padding: 16px;
  max-width: 100%;
  overflow-x: auto;
  
  @media (max-width: 768px) {
    padding: 8px;
  }
`;

const TableWrapper = styled.div`
  .ant-table-wrapper {
    overflow-x: auto;
  }
  
  .ant-table {
    min-width: 600px;
  }
  
  .ant-table-cell {
    white-space: nowrap;
    
    @media (max-width: 768px) {
      padding: 8px 4px !important;
    }
  }
`;

const AddButton = styled(Button)`
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    display: block;
    width: 100%;
    margin-bottom: 8px;
  }
`;

const CasesPage: React.FC = () => {
    const [cases, setCases] = useState<CaseItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [formVisible, setFormVisible] = useState(false);
    const [currentCase, setCurrentCase] = useState<Partial<CaseItem> | null>(null);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastDoc, setLastDoc] = useState<any>(null);
    const pageSize = 10;

    useEffect(() => {
        fetchCases(currentPage);
        fetchTotalCount();
    }, [currentPage]);

    const fetchTotalCount = async () => {
        try {
            const casesCollection = collection(db, 'cases');
            const snapshot = await getCountFromServer(casesCollection);
            const count = snapshot.data().count;
            setTotal(count);

            const maxPage = Math.ceil(count / pageSize);
            if (currentPage > maxPage && maxPage > 0) {
                setCurrentPage(1);
                setLastDoc(null);
            }
        } catch (error) {
            console.error('Error fetching total count:', error);
            setTotal(0);
        }
    };

    const fetchCases = async (page: number) => {
        setLoading(true);
        try {
            const casesCollection = collection(db, 'cases');
            let q;

            if (page === 1) {
                q = query(
                    casesCollection,
                    orderBy('showTime', 'desc'),
                    limit(pageSize)
                );
            } else {
                if (!lastDoc) {
                    q = query(
                        casesCollection,
                        orderBy('showTime', 'desc'),
                        limit(pageSize * (page))
                    );
                } else {
                    q = query(
                        casesCollection,
                        orderBy('showTime', 'desc'),
                        startAfter(lastDoc),
                        limit(pageSize)
                    );
                }
            }

            const casesSnapshot = await getDocs(q);
            const lastVisible = casesSnapshot.docs[casesSnapshot.docs.length - 1];
            setLastDoc(lastVisible);

            const casesData = casesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            })) as CaseItem[];

            setCases(casesData);
        } catch (error) {
            console.error('Error fetching cases:', error);
            message.error('Failed to load cases. Please try again.');
            setCases([]);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (record: CaseItem) => {
        setCurrentCase(record);
        setFormVisible(true);
    };

    const handleAdd = () => {
        setCurrentCase(null);
        setFormVisible(true);
    };

    const handleCancel = () => {
        setFormVisible(false);
        setCurrentCase(null);
    };

    const handleSubmit = async (values: Partial<CaseItem>) => {
        setLoading(true);
        try {
            if (currentCase?.id) {
                const caseRef = doc(db, 'cases', currentCase.id);
                await updateDoc(caseRef, values);
                message.success('Case updated successfully');
            } else {
                await addDoc(collection(db, 'cases'), {
                    ...values,
                    createdAt: Date.now()
                });
                message.success('Case added successfully');
            }
            fetchCases(currentPage);
            fetchTotalCount();
            handleCancel();
        } catch (error) {
            console.error('Error saving case:', error);
            message.error('Failed to save case. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        setLoading(true);
        try {
            const caseRef = doc(db, 'cases', id);
            await deleteDoc(caseRef);
            message.success('Case deleted successfully');
            fetchCases(currentPage);
            fetchTotalCount();
        } catch (error) {
            console.error('Error deleting case:', error);
            message.error('Failed to delete case. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Actions',
            key: 'actions',
            render: (text: string, record: CaseItem) => (
                <>
                    <Button size="small" onClick={() => handleEdit(record)} style={{marginRight: 8}}>Edit</Button>
                    <Popconfirm
                        title="Are you sure you want to delete this case?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button size="small" danger>Delete</Button>
                    </Popconfirm>
                </>
            ),
        },
        {
            title: 'Title (English)',
            dataIndex: ['title', 'en'],
            key: 'titleEn',
            ellipsis: true,
        },
        {
            title: 'Title (Chinese)',
            dataIndex: ['title', 'zh'],
            key: 'titleZh',
            ellipsis: true,
        },
        {
            title: 'Client (English)',
            dataIndex: ['client', 'en'],
            key: 'clientEn',
            ellipsis: true,
        },
        {
            title: 'Exhibition Area (m²)',
            dataIndex: 'exhibitionArea',
            key: 'exhibitionArea',
            ellipsis: true,
        },
    ];

    return (
        <ResponsiveContainer>
            <AddButton type="primary" onClick={handleAdd}>
                Add New Case
            </AddButton>
            <TableWrapper>
                <Table
                    dataSource={cases}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: total,
                        onChange: (page) => {
                            setCurrentPage(page);
                            if (page === 1) {
                                setLastDoc(null);
                            }
                        },
                        hideOnSinglePage: true,
                        showSizeChanger: false,
                        size: 'small',
                        responsive: true,
                    }}
                    scroll={{x: 'max-content'}}
                    size="small"
                />
            </TableWrapper>
            <CaseForm
                visible={formVisible}
                onCancel={handleCancel}
                onSubmit={handleSubmit}
                initialValues={currentCase || undefined}
                loading={loading}
            />
        </ResponsiveContainer>
    );
};

export default CasesPage;
